import type {
  CreateSignatureRequestsBody,
  CreateSignatureRequestsResponse,
} from './types';

import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { queryKeysDeliveryNote } from '../queryKeys';

import { createSignatureRequests } from './createSignatureRequests';

type CreateSignatureRequestsMutationOptions = UseMutationOptions<
  CreateSignatureRequestsResponse,
  Error,
  CreateSignatureRequestsBody
>;

/**
 * Custom hook for creating signature requests for a delivery note.
 *
 * @returns An object containing the mutation function and related properties.
 */
export const useMutationCreateSignatureRequests = (
  options?: CreateSignatureRequestsMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      deliveryNoteIds,
      recipientIds,
    }: CreateSignatureRequestsBody) =>
      createSignatureRequests(deliveryNoteIds, recipientIds),
    onSuccess(_data, variables) {
      void queryClient.invalidateQueries({
        queryKey: [queryKeysDeliveryNote.base(), 'signatureRequestsBulk'],
      });
      for (const deliveryNoteId of variables.deliveryNoteIds) {
        void queryClient.invalidateQueries({
          queryKey: queryKeysDeliveryNote.getSignatureRequests(deliveryNoteId),
        });
      }
    },
    ...options,
  });
};
